import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface CronJob {
  id: string; name: string; command: string; schedule: string
  enabled: number; last_run: string | null; last_status: string | null; created_at: string
}

interface CronLog { id: string; status: string; output: string | null; started_at: string; ended_at: string | null }

const PRESETS = [
  { label: 'Every minute', cron: '* * * * *' },
  { label: 'Every 5 min', cron: '*/5 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Daily midnight', cron: '0 0 * * *' },
  { label: 'Weekly Monday', cron: '0 0 * * 1' },
]

export function CronPanel() {
  const [jobs, setJobs] = useState<CronJob[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [name, setName] = useState('')
  const [command, setCommand] = useState('')
  const [schedule, setSchedule] = useState('0 * * * *')
  const [logsFor, setLogsFor] = useState<string | null>(null)
  const [logs, setLogs] = useState<CronLog[]>([])

  const load = useCallback(() => {
    authFetch(apiUrl('/api/cron')).then(r => r.json()).then(d => setJobs(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!name || !command || !schedule) return
    await authFetch(apiUrl('/api/cron'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, command, schedule }),
    })
    setName(''); setCommand(''); setShowAdd(false); load()
  }

  const toggle = async (id: string, enabled: boolean) => {
    await authFetch(apiUrl(`/api/cron/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !enabled }),
    })
    load()
  }

  const remove = async (id: string) => {
    await authFetch(apiUrl(`/api/cron/${id}`), { method: 'DELETE' })
    load()
  }

  const runNow = async (id: string) => {
    await authFetch(apiUrl(`/api/cron/${id}/run`), { method: 'POST' })
    setTimeout(load, 1500)
  }

  const viewLogs = async (id: string) => {
    setLogsFor(logsFor === id ? null : id)
    if (logsFor !== id) {
      const res = await authFetch(apiUrl(`/api/cron/${id}/logs`))
      setLogs(await res.json())
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>⏰</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Scheduled Jobs</span>
        <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{jobs.length}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAdd(s => !s)} style={btnStyle}>+ New Job</button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ padding: 12, borderBottom: '1px solid var(--border)', background: 'rgba(124,110,245,0.04)', flexShrink: 0 }}>
          <input placeholder="Job name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
          <input placeholder="Command (e.g. node scripts/cleanup.js)" value={command} onChange={e => setCommand(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <input placeholder="Cron expression" value={schedule} onChange={e => setSchedule(e.target.value)} style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {PRESETS.map(p => (
              <button key={p.cron} onClick={() => setSchedule(p.cron)} style={{
                ...btnStyle, fontSize: 8,
                background: schedule === p.cron ? 'rgba(124,110,245,0.15)' : 'var(--bg-2)',
                color: schedule === p.cron ? 'var(--accent)' : 'var(--text-1)',
              }}>{p.label}</button>
            ))}
          </div>
          <button onClick={create} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>Create Job</button>
        </div>
      )}

      {/* Jobs list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {jobs.length === 0 && !showAdd && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            No scheduled jobs. Create one to run scripts automatically.
          </div>
        )}
        {jobs.map(j => (
          <div key={j.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => toggle(j.id, !!j.enabled)} style={{
                width: 28, height: 16, borderRadius: 8, border: 'none', cursor: 'pointer',
                background: j.enabled ? 'var(--accent-2)' : 'var(--bg-3)', position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 2, left: j.enabled ? 14 : 2, transition: 'left 0.2s',
                }} />
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: j.enabled ? 'var(--text-0)' : 'var(--text-2)' }}>{j.name}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                  <code style={{ background: 'var(--bg-3)', padding: '1px 4px', borderRadius: 3 }}>{j.schedule}</code>
                  {' · '}{j.command}
                </div>
              </div>
              {j.last_status && (
                <span style={{
                  fontSize: 8, padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)', fontWeight: 700,
                  background: j.last_status === 'completed' ? 'rgba(95,212,164,0.12)' : j.last_status === 'running' ? 'rgba(124,110,245,0.12)' : 'rgba(240,112,112,0.12)',
                  color: j.last_status === 'completed' ? 'var(--accent-2)' : j.last_status === 'running' ? 'var(--accent)' : 'var(--red)',
                }}>{j.last_status}</span>
              )}
              <button onClick={() => runNow(j.id)} style={btnStyle} title="Run now">▶</button>
              <button onClick={() => viewLogs(j.id)} style={btnStyle}>Logs</button>
              <button onClick={() => remove(j.id)} style={{ ...btnStyle, color: 'var(--red)' }}>✕</button>
            </div>

            {/* Logs */}
            {logsFor === j.id && (
              <div style={{ padding: '0 14px 10px 46px', maxHeight: 150, overflow: 'auto' }}>
                {logs.length === 0 && <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>No logs yet</div>}
                {logs.map(l => (
                  <div key={l.id} style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: l.status === 'completed' ? 'var(--accent-2)' : 'var(--red)' }}>{l.status}</span>
                    {' · '}{l.started_at}
                    {l.output && <div style={{ color: 'var(--text-2)', marginTop: 2 }}>{l.output}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer',
  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', marginBottom: 6, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)',
  fontSize: 11, fontFamily: 'var(--font-mono)', outline: 'none',
}

